import React, { FC } from 'react';
import { css } from '@emotion/core';

import { useRestartUFOComponentExperience } from '@/common/ufo';
import { reports } from '@/meta';
import { grid, MOBILE_BREAKPOINT, SPACE_MINIMUM } from '@/styles';
import { Report } from '@/components/primitive';
import { getSLOFilterQuery, useRefreshController, filterStore } from '@/controllers';
import { reportViewExperience } from '@/common/ufo/experiences';

import { queryStore } from './catalog-search-bar/stores/query';
import * as queryUtils from './catalog-search-bar/services/utils';

const styles = {
  reports: css`
    padding: ${SPACE_MINIMUM}px;
    grid-template-columns: repeat(2, 1fr);

    @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      grid-template-columns: 1fr;
    }
  `,
  capabilities: css`
    grid-column: 1 / 3;

    @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
      grid-column: 1 / 2;
    }
  `,
};

interface IProps {
  query: string;
}

const capabilityMeta = reports.capability;
// not as nice as Object.entries but satisfies tsc
const reportables = [
  ['product', reports.product],
  ['team', reports.team],
] as const;

const Reports: FC<IProps> = ({ query }) => {
  useRestartUFOComponentExperience(reportViewExperience, [query]);
  // pulls out specific query for each widget
  const refreshController = useRefreshController();

  const { state: queryState } = React.useContext(queryStore);
  const { state: filterState } = React.useContext(filterStore);

  const capabilityParams = queryUtils.mapParamsToAPIQuery(queryState.params, true);
  const capabilityFilter = getSLOFilterQuery(filterState.slo);
  const capabilityAPIQuery = `${capabilityParams}${capabilityFilter}`;

  return (
    <div css={[grid, styles.reports]}>
      <Report
        key="capability"
        type="capability"
        css={styles.capabilities}
        copy={capabilityMeta.copy}
        url={`${capabilityMeta.endpoints.report}${refreshController.getQuery(
          capabilityAPIQuery,
          'capability'
        )}`}
      />
      {reportables.map(([reportType, { copy, endpoints }]) => {
        let reportTypeQuery: string = refreshController.getQuery(query, reportType);

        // "/team/search" endpoint does not support sorting by hasQualityIssues
        // TODO: use global QueryMap to get any "endpoints.report" query url so we
        // don't have to manually build the url fix everywhere
        if (reportType === 'team') {
          reportTypeQuery = reportTypeQuery.replace('sort=hasQualityIssues%2Cdesc', '');
        }
        return (
          <Report
            key={reportType}
            type={reportType}
            copy={copy}
            url={`${endpoints.report}${reportTypeQuery}`}
          />
        );
      })}
    </div>
  );
};

export default React.memo(Reports);
